import React from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SelectFarmWarehouse from "../../../components/SelectFarmWarehouse";
import { getAPIDataParamsReturn } from "../../../utils/apiFunction";
import { endOfWeekSun, startOfWeekMon } from "../../../utils/dateFunctions";
import { numberFormatErrorToZero as numberFormat } from "../../../utils/numberFunctions";

const NULL_FILTER_TOKEN = "__NULL__";

const graphDecimalFormatter = new Intl.NumberFormat("en-AU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const emptyFilterOptions = {
    cropYears: [],
    suppliers: [],
    stockTypes: [],
    cropDates: [],
};

const emptyFilterBatches = [];

const emptyFilters = {
    cropYear: "",
    supplierId: "",
    stockType: "",
    cropDate: "",
};

const formatGroupQuantity = (group) => {
    if (!group) {
        return "";
    }

    const totalUnits = numberFormat(group.totalUnits || 0);
    const average = numberFormat(group.average || 0);
    const unitType = group.unitType || "units";

    return `${totalUnits} ${unitType} | avg ${average}`;
};

const getGroupOysterCount = (group) => {
    if (!group) {
        return 0;
    }

    const oysterCount = Number(group.oysterCount);
    if (!Number.isNaN(oysterCount) && oysterCount > 0) {
        return oysterCount;
    }

    return (Number(group.totalUnits) || 0) * (Number(group.average) || 0);
};

const getOutputOysterCount = (outputs) => {
    return (outputs || []).reduce((total, output) => total + getGroupOysterCount(output), 0);
};

const getVariancePercentage = (input, outputs) => {
    const inputTotal = getGroupOysterCount(input);

    if (inputTotal <= 0) {
        return null;
    }

    const outputTotal = getOutputOysterCount(outputs);
    return ((outputTotal - inputTotal) / inputTotal) * 100;
};

const formatGraphDecimalValue = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return "";
    }

    return graphDecimalFormatter.format(numericValue);
};

const formatOysterTotal = (oysterCount) => {
    const total = Number(oysterCount) || 0;
    const dozens = Math.round(total / 12);

    return `${numberFormat(total)} pcs (${numberFormat(dozens)} doz)`;
};

const getDozenCount = (oysterCount) => {
    return Math.round((Number(oysterCount) || 0) / 12);
};

const parseDateValue = (value) => {
    if (!value) {
        return null;
    }

    const trimmedValue = `${value}`.trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmedValue);

    if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }

    const dateValue = new Date(trimmedValue);
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
};

const getAustralianSeasonInfo = (dateValue) => {
    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return null;
    }

    const month = dateValue.getMonth();
    const year = dateValue.getFullYear();

    if (month === 11 || month === 0 || month === 1) {
        const seasonStartYear = month === 11 ? year : year - 1;

        return {
            key: `${seasonStartYear}-12`,
            label: `Summer ${seasonStartYear}/${seasonStartYear + 1}`,
        };
    }

    if (month >= 2 && month <= 4) {
        return {
            key: `${year}-03`,
            label: `Autumn ${year}`,
        };
    }

    if (month >= 5 && month <= 7) {
        return {
            key: `${year}-06`,
            label: `Winter ${year}`,
        };
    }

    return {
        key: `${year}-09`,
        label: `Spring ${year}`,
    };
};

const formatDateKey = (dateValue) => {
    return `${dateValue.getFullYear()}-${`${dateValue.getMonth() + 1}`.padStart(2, "0")}-${`${dateValue.getDate()}`.padStart(2, "0")}`;
};

const getWeekInfo = (dateValue) => {
    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return null;
    }

    const weekStart = startOfWeekMon(dateValue);
    const weekEnd = endOfWeekSun(dateValue);

    return {
        key: formatDateKey(weekStart),
        label: `Week ${formatDisplayDate(weekStart)} to ${formatDisplayDate(weekEnd)}`,
    };
};

const formatVariancePercentage = (variance) => {
    if (variance == null || Number.isNaN(variance)) {
        return "-";
    }

    const roundedVariance = Math.abs(variance) < 0.05 ? 0 : variance;
    const prefix = roundedVariance > 0 ? "+" : "";

    return `${prefix}${roundedVariance.toFixed(1)}%`;
};

const formatDisplayDate = (value) => {
    if (!value) {
        return "";
    }

    const trimmedValue = `${value}`.trim();
    const dateValue = parseDateValue(trimmedValue);

    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return trimmedValue;
    }

    return new Intl.DateTimeFormat("en-AU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(dateValue);
};

const getGroupByLabel = (groupBy) => {
    if (groupBy === "week") {
        return "Group by Week";
    }

    if (groupBy === "month") {
        return "Group by Month";
    }

    if (groupBy === "season") {
        return "Group by Season";
    }

    if (groupBy === "year") {
        return "Group by Year";
    }

    return "No grouping";
};

const getBatchLabel = (row) => {
    if (!row) {
        return "";
    }

    if (row.batch) {
        return row.batch;
    }

    const crop = row.crop || row.cropYear || "";
    const stockType = row.stockType || "";
    const supplier = row.supplier || "";

    if (!crop) {
        return `${stockType} ${supplier}`.trim();
    }

    return `${crop}-${stockType} ${supplier}`.trim();
};

const getRowKey = (row) => {
    return `${row.input?.groupId || "input"}-${row.eventDate}-${(row.outputs || []).map((output) => output.groupId).join("-")}`;
};

const getGroupHeaderLabel = (value, groupBy) => {
    const dateValue = parseDateValue(value);

    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return value || "Ungrouped";
    }

    if (groupBy === "week") {
        return getWeekInfo(dateValue)?.label || "Ungrouped";
    }

    if (groupBy === "season") {
        return getAustralianSeasonInfo(dateValue)?.label || "Ungrouped";
    }

    if (groupBy === "year") {
        return new Intl.DateTimeFormat("en-AU", { year: "numeric" }).format(dateValue);
    }

    return new Intl.DateTimeFormat("en-AU", { month: "long", year: "numeric" }).format(dateValue);
};

const getGroupKey = (row, groupBy) => {
    if (groupBy === "none") {
        return "all";
    }

    const dateValue = parseDateValue(row?.eventDate);
    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return `unknown-${row?.eventDate || ""}`;
    }

    if (groupBy === "week") {
        return getWeekInfo(dateValue)?.key || `unknown-${row?.eventDate || ""}`;
    }

    if (groupBy === "season") {
        return getAustralianSeasonInfo(dateValue)?.key || `unknown-${row?.eventDate || ""}`;
    }

    if (groupBy === "year") {
        return `${dateValue.getFullYear()}`;
    }

    return `${dateValue.getFullYear()}-${`${dateValue.getMonth() + 1}`.padStart(2, "0")}`;
};

const buildRowGroups = (rows, groupBy) => {
    if (groupBy === "none") {
        return [{ key: "all", label: "", rows }];
    }

    const groups = [];
    const groupsByKey = new Map();

    rows.forEach((row) => {
        const groupKey = getGroupKey(row, groupBy);

        if (!groupsByKey.has(groupKey)) {
            const nextGroup = {
                key: groupKey,
                label: getGroupHeaderLabel(row?.eventDate, groupBy),
                rows: [],
            };

            groupsByKey.set(groupKey, nextGroup);
            groups.push(nextGroup);
        }

        groupsByKey.get(groupKey).rows.push(row);
    });

    return groups;
};

const getGroupedTotals = (groupRows) => {
    const inputTotal = groupRows.reduce((total, row) => total + getGroupOysterCount(row.input), 0);
    const outputTotal = groupRows.reduce((total, row) => total + getOutputOysterCount(row.outputs), 0);
    const variance = inputTotal > 0 ? ((outputTotal - inputTotal) / inputTotal) * 100 : null;

    return {
        inputTotal,
        outputTotal,
        variance,
    };
};

const getGraphGroupBy = (groupBy) => {
    if (groupBy === "week") {
        return "week";
    }

    if (groupBy === "year") {
        return "year";
    }

    if (groupBy === "season") {
        return "season";
    }

    return "month";
};

const buildGraphData = (rows, groupBy) => {
    const effectiveGroupBy = getGraphGroupBy(groupBy);

    return buildRowGroups(rows, effectiveGroupBy)
        .map((group) => {
            const totals = getGroupedTotals(group.rows);

            return {
                key: group.key,
                label: group.label,
                inputTotal: totals.inputTotal,
                variance: totals.variance,
            };
        })
        .sort((left, right) => left.key.localeCompare(right.key));
};

const getOutputBreakdownText = (outputs) => {
    if (!Array.isArray(outputs) || outputs.length === 0) {
        return "No outputs recorded";
    }

    return outputs
        .map((output) => `${renderOutputGrade(output)}: ${formatOysterTotal(getGroupOysterCount(output))} | ${formatGroupQuantity(output)}`)
        .join("\n");
};

const downloadExcelWorkbook = (buffer, fileName) => {
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const BatchGradingSummaryChart = ({ rows, groupBy }) => {
    const canvasRef = React.useRef(null);
    const chartRef = React.useRef(null);
    const effectiveGroupBy = getGraphGroupBy(groupBy);
    const graphData = React.useMemo(() => buildGraphData(rows, groupBy), [rows, groupBy]);

    React.useEffect(() => {
        if (!canvasRef.current || graphData.length === 0) {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }

            return undefined;
        }

        let cancelled = false;
        const canvas = canvasRef.current;

        const renderChart = async () => {
            const chartModule = await import("chart.js/auto");
            const Chart = chartModule.default;

            if (cancelled || !canvas) {
                return;
            }

            if (chartRef.current) {
                chartRef.current.destroy();
            }

            chartRef.current = new Chart(canvas, {
                data: {
                    labels: graphData.map((item) => item.label),
                    datasets: [
                        {
                            type: "bar",
                            label: "Total Input",
                            data: graphData.map((item) => item.inputTotal),
                            yAxisID: "input",
                            backgroundColor: "rgba(25, 118, 210, 0.55)",
                            borderColor: "rgba(25, 118, 210, 1)",
                            borderWidth: 1,
                            borderRadius: 6,
                            maxBarThickness: 44,
                        },
                        {
                            type: "line",
                            label: "Variance %",
                            data: graphData.map((item) => item.variance),
                            yAxisID: "variance",
                            borderColor: "rgba(239, 108, 0, 1)",
                            backgroundColor: "rgba(239, 108, 0, 0.15)",
                            tension: 0.35,
                            fill: false,
                            pointRadius: 4,
                            pointHoverRadius: 5,
                            spanGaps: true,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: "index",
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            position: "top",
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    if (context.dataset.yAxisID === "variance") {
                                        return `${context.dataset.label}: ${formatGraphDecimalValue(context.raw)}%`;
                                    }

                                    return `${context.dataset.label}: ${numberFormat(context.raw)} pcs (${numberFormat(getDozenCount(context.raw))} doz)`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            title: {
                                display: true,
                                text: effectiveGroupBy === "week"
                                    ? "Week"
                                    : effectiveGroupBy === "year"
                                    ? "Year"
                                    : effectiveGroupBy === "season"
                                        ? "Season"
                                        : "Month",
                            },
                        },
                        input: {
                            type: "linear",
                            position: "left",
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: "Total Input (pcs)",
                            },
                            ticks: {
                                callback: (value) => numberFormat(value),
                            },
                        },
                        variance: {
                            type: "linear",
                            position: "right",
                            title: {
                                display: true,
                                text: "Variance (%)",
                            },
                            ticks: {
                                callback: (value) => `${formatGraphDecimalValue(value)}%`,
                            },
                            grid: {
                                drawOnChartArea: false,
                            },
                        },
                    },
                },
            });
        };

        renderChart();

        return () => {
            cancelled = true;

            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [effectiveGroupBy, graphData]);

    if (graphData.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No grouped values are available to graph for the current report.
            </Typography>
        );
    }

    return (
        <Stack spacing={1.5}>
            <Typography variant="caption" color="text.secondary">
                {effectiveGroupBy === "week"
                    ? "Showing weekly grouped totals using Monday to Sunday periods. Total input is shown as bars and variance percentage as a curved line."
                    : effectiveGroupBy === "year"
                    ? "Showing yearly grouped totals. Total input is shown as bars and variance percentage as a curved line."
                    : effectiveGroupBy === "season"
                        ? "Showing Australian seasonal grouped totals. Summer is December to February, Autumn is March to May, Winter is June to August, and Spring is September to November."
                        : "Showing monthly grouped totals. Total input is shown as bars and variance percentage as a curved line."}
            </Typography>
            <div style={{ height: 420, position: "relative" }}>
                <canvas ref={canvasRef} />
            </div>
        </Stack>
    );
};

const renderOutputGrade = (group) => {
    if (!group) {
        return "";
    }

    if (Array.isArray(group.mixedGrades) && group.mixedGrades.length > 0) {
        const breakdown = group.mixedGrades
            .map((item) => `${item.grade} ${item.percentage}%`)
            .join(" | ");

        return `${group.grade} (${breakdown})`;
    }

    return group.grade || "";
};

const normaliseStringOptions = (values, preserveNullOption = false) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const seen = new Set();

    return values.reduce((accumulator, value) => {
        const nextValue = value == null || `${value}`.trim() === ""
            ? (preserveNullOption ? NULL_FILTER_TOKEN : null)
            : `${value}`;

        if (!nextValue || seen.has(nextValue)) {
            return accumulator;
        }

        seen.add(nextValue);
        accumulator.push(nextValue);
        return accumulator;
    }, []);
};

const sortCropYears = (values) => {
    return [...values].sort((left, right) => {
        const leftIsNull = left === NULL_FILTER_TOKEN;
        const rightIsNull = right === NULL_FILTER_TOKEN;

        if (leftIsNull !== rightIsNull) {
            return leftIsNull ? 1 : -1;
        }

        return `${right}`.localeCompare(`${left}`, undefined, { numeric: true, sensitivity: "base" });
    });
};

const normaliseBatchOptions = (values) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const seen = new Set();

    return values.reduce((accumulator, option) => {
        if (!option) {
            return accumulator;
        }

        const cropYear = option.cropYear == null || `${option.cropYear}`.trim() === ""
            ? NULL_FILTER_TOKEN
            : `${option.cropYear}`.trim();
        const supplierId = option.supplierId == null ? "" : `${option.supplierId}`;
        const supplierKey = option.supplierKey == null || `${option.supplierKey}`.trim() === ""
            ? supplierId
            : `${option.supplierKey}`.trim();
        const stockType = option.stockType == null ? "" : `${option.stockType}`.trim();
        const stockTypeLabel = option.stockTypeLabel || stockType;
        const cropDate = option.cropDate == null ? "" : `${option.cropDate}`.trim();

        if (!supplierKey || !stockType || !cropDate) {
            return accumulator;
        }

        const key = `${cropYear}|${supplierKey}|${stockType}|${cropDate}`;
        if (seen.has(key)) {
            return accumulator;
        }

        seen.add(key);

        accumulator.push({
            batch: option.batch || "",
            cropYear,
            supplierId: supplierKey,
            supplierName: option.supplierName || "Unnamed supplier",
            stockType,
            stockTypeLabel,
            cropDate,
        });

        return accumulator;
    }, []);
};

const matchesBatchFilters = (batch, filters, ignoredField) => {
    if (!batch) {
        return false;
    }

    return ["cropYear", "supplierId", "stockType", "cropDate"].every((field) => {
        if (field === ignoredField) {
            return true;
        }

        return !filters[field] || batch[field] === filters[field];
    });
};

const buildSupplierOptions = (batches) => {
    const suppliers = new Map();

    batches.forEach((batch) => {
        if (!batch?.supplierId) {
            return;
        }

        const nextName = batch.supplierName || "Unnamed supplier";
        const existing = suppliers.get(batch.supplierId);

        if (!existing || (existing.name !== "Mixed Suppliers" && nextName === "Mixed Suppliers")) {
            suppliers.set(batch.supplierId, { id: batch.supplierId, name: nextName });
        }
    });

    return Array.from(suppliers.values()).sort((left, right) => left.name.localeCompare(right.name));
};

const buildStockTypeOptions = (batches) => {
    const stockTypes = new Map();
    const getSortOrder = (value) => (value === "WLD" ? 0 : value === "HAT" ? 1 : 2);

    batches.forEach((batch) => {
        if (!batch?.stockType || stockTypes.has(batch.stockType)) {
            return;
        }

        stockTypes.set(batch.stockType, {
            id: batch.stockType,
            name: batch.stockTypeLabel || batch.stockType,
        });
    });

    return Array.from(stockTypes.values()).sort((left, right) => getSortOrder(left.id) - getSortOrder(right.id));
};

const buildFilterOptions = (batches, filters) => {
    if (!Array.isArray(batches) || batches.length === 0) {
        return emptyFilterOptions;
    }

    const getMatchingBatches = (ignoredField) => batches.filter((batch) => matchesBatchFilters(batch, filters, ignoredField));

    return {
        cropYears: sortCropYears(normaliseStringOptions(getMatchingBatches("cropYear").map((batch) => batch.cropYear), true)),
        suppliers: buildSupplierOptions(getMatchingBatches("supplierId")),
        stockTypes: buildStockTypeOptions(getMatchingBatches("stockType")),
        cropDates: normaliseStringOptions(getMatchingBatches("cropDate").map((batch) => batch.cropDate)),
    };
};

const sanitiseFilters = (filters, filterOptions) => {
    const nextFilters = { ...filters };

    if (nextFilters.cropYear && !filterOptions.cropYears.includes(nextFilters.cropYear)) {
        nextFilters.cropYear = "";
    }

    if (nextFilters.supplierId && !filterOptions.suppliers.some((option) => option.id === nextFilters.supplierId)) {
        nextFilters.supplierId = "";
    }

    if (nextFilters.stockType && !filterOptions.stockTypes.some((option) => option.id === nextFilters.stockType)) {
        nextFilters.stockType = "";
    }

    if (nextFilters.cropDate && !filterOptions.cropDates.includes(nextFilters.cropDate)) {
        nextFilters.cropDate = "";
    }

    return nextFilters;
};

const areFiltersEqual = (left, right) => (
    left.cropYear === right.cropYear
    && left.supplierId === right.supplierId
    && left.stockType === right.stockType
    && left.cropDate === right.cropDate
);

const resolveFilterState = (batches, filters) => {
    let nextFilters = { ...filters };

    while (true) {
        const nextOptions = buildFilterOptions(batches, nextFilters);
        const sanitised = sanitiseFilters(nextFilters, nextOptions);

        if (areFiltersEqual(nextFilters, sanitised)) {
            return {
                filters: sanitised,
                options: nextOptions,
            };
        }

        nextFilters = sanitised;
    }
};

const getCropYearLabel = (value) => {
    return value === NULL_FILTER_TOKEN ? "No crop year" : value;
};

const BatchGrading = ({ access }) => {
    const [farm, setFarm] = React.useState(null);
    const [filters, setFilters] = React.useState(emptyFilters);
    const [filterBatches, setFilterBatches] = React.useState(emptyFilterBatches);
    const [rows, setRows] = React.useState([]);
    const [selectedRowKey, setSelectedRowKey] = React.useState("");
    const [groupBy, setGroupBy] = React.useState("none");
    const [showGraph, setShowGraph] = React.useState(false);
    const [loadingFilters, setLoadingFilters] = React.useState(false);
    const [loadingReport, setLoadingReport] = React.useState(false);
    const [exporting, setExporting] = React.useState(false);
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    const [error, setError] = React.useState("");
    const resolvedFilterState = resolveFilterState(filterBatches, filters);
    const activeFilters = resolvedFilterState.filters;
    const filterOptions = resolvedFilterState.options;
    const rowGroups = buildRowGroups(rows, groupBy);

    React.useEffect(() => {
        const abortController = new AbortController();

        if (!farm?.warehouseId) {
            setFilterBatches(emptyFilterBatches);
            setFilters(emptyFilters);
            setRows([]);
            setSelectedRowKey("");
            setHasSubmitted(false);
            setError("");
            return () => abortController.abort();
        }

        setLoadingFilters(true);
        setFilterBatches(emptyFilterBatches);
        setFilters(emptyFilters);
        setRows([]);
        setSelectedRowKey("");
        setHasSubmitted(false);
        setError("");

        getAPIDataParamsReturn(
            "/Farms/BatchReporting/BatchGradingFilters",
            { WarehouseId: farm.warehouseId },
            abortController
        )
            .then((data) => {
                setFilterBatches(normaliseBatchOptions(data?.batches));
            })
            .catch(() => {
                setFilterBatches(emptyFilterBatches);
                setError("Unable to load Batch Grading filters.");
            })
            .finally(() => setLoadingFilters(false));

        return () => abortController.abort();
    }, [farm?.warehouseId]);

    React.useEffect(() => {
        const filtersMatch = (
            filters.cropYear === activeFilters.cropYear
            && filters.supplierId === activeFilters.supplierId
            && filters.stockType === activeFilters.stockType
            && filters.cropDate === activeFilters.cropDate
        );

        if (filtersMatch) {
            return;
        }

        setFilters({
            cropYear: activeFilters.cropYear,
            supplierId: activeFilters.supplierId,
            stockType: activeFilters.stockType,
            cropDate: activeFilters.cropDate,
        });
    }, [
        filters.cropDate,
        filters.cropYear,
        filters.stockType,
        filters.supplierId,
        activeFilters.cropDate,
        activeFilters.cropYear,
        activeFilters.stockType,
        activeFilters.supplierId,
    ]);

    const handleFilterChange = (field) => (event) => {
        const nextValue = event.target.value;
        setFilters((current) => ({ ...current, [field]: nextValue }));
    };

    const handleSubmit = async () => {
        if (!farm?.warehouseId) {
            return;
        }

        setLoadingReport(true);
        setHasSubmitted(true);
        setSelectedRowKey("");
        setError("");

        try {
            const data = await getAPIDataParamsReturn("/Farms/BatchReporting/BatchGradingReport", {
                WarehouseId: farm.warehouseId,
                CropYear: activeFilters.cropYear || undefined,
                SupplierId: activeFilters.supplierId || undefined,
                StockType: activeFilters.stockType || undefined,
                CropDate: activeFilters.cropDate || undefined,
            });

            setRows(Array.isArray(data) ? data : []);
        }
        catch {
            setRows([]);
            setError("Unable to load the Batch Grading report.");
        }
        finally {
            setLoadingReport(false);
        }
    };

    const handleExportExcel = async () => {
        if (rows.length === 0) {
            return;
        }

        const supplierLabel = activeFilters.supplierId
            ? filterOptions.suppliers.find((option) => option.id === activeFilters.supplierId)?.name || activeFilters.supplierId
            : "All suppliers";
        const stockTypeLabel = activeFilters.stockType
            ? filterOptions.stockTypes.find((option) => option.id === activeFilters.stockType)?.name || activeFilters.stockType
            : "All stock types";
        const warehouseLabel = farm?.warehouseName || farm?.warehouseDescription || (farm?.warehouseId ? `Warehouse ${farm.warehouseId}` : "");

        setExporting(true);
        setError("");

        try {
            const ExcelJS = (await import("exceljs")).default;
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Batch Grading");
            const generatedAt = new Date();
            const fileDate = generatedAt.toISOString().slice(0, 10);
            const headerLabels = [
                "Section",
                "Date",
                "Crop Date",
                "Batch",
                "Input Grade",
                "Input Quantity",
                "Input Total Pcs",
                "Input Total Doz",
                "Output Total Pcs",
                "Output Total Doz",
                "Variance",
                "Output Breakdown",
            ];

            const titleRow = worksheet.addRow(["Batch Grading Report"]);
            worksheet.mergeCells(1, 1, 1, headerLabels.length);
            titleRow.font = { size: 16, bold: true };
            titleRow.alignment = { horizontal: "center" };

            [
                ["Generated", new Intl.DateTimeFormat("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(generatedAt)],
                ["Warehouse", warehouseLabel],
                ["Group By", getGroupByLabel(groupBy)],
                ["Crop Year", activeFilters.cropYear ? getCropYearLabel(activeFilters.cropYear) : "All crop years"],
                ["Supplier", supplierLabel],
                ["Stock Type", stockTypeLabel],
                ["Crop Date", activeFilters.cropDate ? formatDisplayDate(activeFilters.cropDate) : "All crop dates"],
            ].forEach((rowValues) => {
                worksheet.addRow(rowValues);
            });

            worksheet.addRow([]);

            const headerRow = worksheet.addRow(headerLabels);
            headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
            headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
            headerRow.eachCell((cell) => {
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1976D2" } };
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" },
                    bottom: { style: "thin" },
                };
            });

            worksheet.views = [{ state: "frozen", ySplit: headerRow.number }];

            rowGroups.forEach((group) => {
                if (groupBy !== "none") {
                    const totals = getGroupedTotals(group.rows);
                    const groupRow = worksheet.addRow([
                        group.label,
                        "",
                        "",
                        "",
                        "",
                        "Group totals",
                        totals.inputTotal,
                        getDozenCount(totals.inputTotal),
                        totals.outputTotal,
                        getDozenCount(totals.outputTotal),
                        totals.variance == null ? "" : totals.variance / 100,
                        "",
                    ]);

                    groupRow.font = { bold: true };
                    groupRow.eachCell((cell) => {
                        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3F2FD" } };
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            right: { style: "thin" },
                            bottom: { style: "thin" },
                        };
                    });
                }

                group.rows.forEach((row) => {
                    const inputTotal = getGroupOysterCount(row.input);
                    const outputTotal = getOutputOysterCount(row.outputs);
                    const variance = getVariancePercentage(row.input, row.outputs);
                    const dataRow = worksheet.addRow([
                        groupBy === "none" ? "" : group.label,
                        formatDisplayDate(row.eventDate),
                        formatDisplayDate(row.cropDate),
                        getBatchLabel(row),
                        row.input?.grade || "",
                        formatGroupQuantity(row.input),
                        inputTotal,
                        getDozenCount(inputTotal),
                        outputTotal,
                        getDozenCount(outputTotal),
                        variance == null ? "" : variance / 100,
                        getOutputBreakdownText(row.outputs),
                    ]);

                    dataRow.alignment = { vertical: "top", wrapText: true };
                    dataRow.eachCell((cell) => {
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            right: { style: "thin" },
                            bottom: { style: "thin" },
                        };
                    });
                });
            });

            [14, 12, 12, 32, 18, 24, 16, 16, 16, 16, 12, 48].forEach((width, index) => {
                worksheet.getColumn(index + 1).width = width;
            });

            worksheet.getColumn(7).numFmt = "#,##0";
            worksheet.getColumn(8).numFmt = "#,##0";
            worksheet.getColumn(9).numFmt = "#,##0";
            worksheet.getColumn(10).numFmt = "#,##0";
            worksheet.getColumn(11).numFmt = "0.0%";

            const buffer = await workbook.xlsx.writeBuffer();
            downloadExcelWorkbook(buffer, `BatchGradingReport_${fileDate}.xlsx`);
        }
        catch (exportError) {
            console.error("Batch grading Excel export failed", exportError);
            setError("Unable to export the Batch Grading report to Excel.");
        }
        finally {
            setExporting(false);
        }
    };

    const filtersDisabled = !farm?.warehouseId || loadingFilters;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={4}>
                <SelectFarmWarehouse setState={setFarm} state={farm} access={access} />
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Batch Grading" subheader="Filter grading history by warehouse batch details, then load the matching grade events." />
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Crop Year"
                                    value={activeFilters.cropYear}
                                    onChange={handleFilterChange("cropYear")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All crop years</MenuItem>
                                    {filterOptions.cropYears.map((option) => (
                                        <MenuItem key={option} value={option}>
                                                {getCropYearLabel(option)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Supplier"
                                    value={activeFilters.supplierId}
                                    onChange={handleFilterChange("supplierId")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All suppliers</MenuItem>
                                    {filterOptions.suppliers.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Stock Type"
                                    value={activeFilters.stockType}
                                    onChange={handleFilterChange("stockType")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All stock types</MenuItem>
                                    {filterOptions.stockTypes.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Crop Date"
                                    value={activeFilters.cropDate}
                                    onChange={handleFilterChange("cropDate")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All crop dates</MenuItem>
                                    {filterOptions.cropDates.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {formatDisplayDate(option)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6} lg={2}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleSubmit}
                                    disabled={!farm?.warehouseId || loadingFilters || loadingReport}
                                    sx={{ minHeight: 56 }}
                                >
                                    {loadingReport ? "Loading..." : "Submit"}
                                </Button>
                            </Grid>
                        </Grid>

                        {loadingFilters ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2">Loading warehouse filters...</Typography>
                            </Stack>
                        ) : null}

                        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Report Results"
                        action={rows.length > 0 ? (
                            <Button
                                variant="outlined"
                                onClick={() => setShowGraph((current) => !current)}
                                disabled={loadingReport}
                            >
                                {showGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        ) : null}
                    />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading Batch Grading report...</Typography>
                            </Stack>
                        ) : (
                            <React.Fragment>
                                {rows.length > 0 ? (
                                    <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                                        <Grid item xs={12} md={4} lg={3}>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="Group Report"
                                                value={groupBy}
                                                onChange={(event) => setGroupBy(event.target.value)}
                                            >
                                                <MenuItem value="none">No grouping</MenuItem>
                                                <MenuItem value="week">Group by Week</MenuItem>
                                                <MenuItem value="month">Group by Month</MenuItem>
                                                <MenuItem value="season">Group by Season</MenuItem>
                                                <MenuItem value="year">Group by Year</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} md={4} lg={2}>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={handleExportExcel}
                                                disabled={rows.length === 0 || exporting}
                                            >
                                                {exporting ? "Exporting..." : "Export Excel"}
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} md={4} lg={7}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                {showGraph
                                                    ? "The graph uses the current Group Report selection. Week grouping uses Monday to Sunday periods, and season grouping uses Australian seasons."
                                                    : "Select a row to view the detailed input and output breakdown."}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                ) : null}
                                {showGraph && rows.length > 0 ? (
                                    <BatchGradingSummaryChart rows={rows} groupBy={groupBy} />
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Crop Date</TableCell>
                                                    <TableCell>Batch</TableCell>
                                                    <TableCell>Input Grade</TableCell>
                                                    <TableCell>Input Total Oysters</TableCell>
                                                    <TableCell>Output Total Oysters</TableCell>
                                                    <TableCell>Variance</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rowGroups.map((group) => {
                                                    const totals = getGroupedTotals(group.rows);

                                                    return (
                                                        <React.Fragment key={group.key}>
                                                            {groupBy !== "none" ? (
                                                                <TableRow>
                                                                    <TableCell colSpan={7} sx={{ backgroundColor: "action.selected", py: 1.5 }}>
                                                                        <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 0.5, md: 3 }}>
                                                                            <Typography variant="subtitle2">{group.label}</Typography>
                                                                            <Typography variant="body2">Total Input: {formatOysterTotal(totals.inputTotal)}</Typography>
                                                                            <Typography variant="body2">Total Output: {formatOysterTotal(totals.outputTotal)}</Typography>
                                                                            <Typography variant="body2">Variance: {formatVariancePercentage(totals.variance)}</Typography>
                                                                        </Stack>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : null}

                                                            {group.rows.map((row) => {
                                                                const rowKey = getRowKey(row);
                                                                const isSelected = selectedRowKey === rowKey;
                                                                const variance = getVariancePercentage(row.input, row.outputs);

                                                                return (
                                                                    <React.Fragment key={rowKey}>
                                                                        <TableRow
                                                                            hover
                                                                            selected={isSelected}
                                                                            onClick={() => setSelectedRowKey((current) => current === rowKey ? "" : rowKey)}
                                                                            sx={{
                                                                                cursor: "pointer",
                                                                                "& > *": {
                                                                                    borderBottom: isSelected ? 0 : undefined,
                                                                                },
                                                                            }}
                                                                        >
                                                                            <TableCell>{formatDisplayDate(row.eventDate)}</TableCell>
                                                                            <TableCell>{formatDisplayDate(row.cropDate)}</TableCell>
                                                                            <TableCell>{getBatchLabel(row)}</TableCell>
                                                                            <TableCell>{row.input?.grade || ""}</TableCell>
                                                                            <TableCell>{formatOysterTotal(getGroupOysterCount(row.input))}</TableCell>
                                                                            <TableCell>{formatOysterTotal(getOutputOysterCount(row.outputs))}</TableCell>
                                                                            <TableCell>{formatVariancePercentage(variance)}</TableCell>
                                                                        </TableRow>

                                                                        {isSelected ? (
                                                                            <TableRow>
                                                                                <TableCell colSpan={7} sx={{ backgroundColor: "action.hover", py: 2.5 }}>
                                                                                    <Grid container spacing={2}>
                                                                                        <Grid item xs={12} md={4}>
                                                                                            <Typography variant="subtitle2" gutterBottom>Input</Typography>
                                                                                            <Stack spacing={0.5}>
                                                                                                <Typography variant="body2" fontWeight={600}>{row.input?.grade || ""}</Typography>
                                                                                                <Typography variant="body2">{formatOysterTotal(getGroupOysterCount(row.input))}</Typography>
                                                                                                <Typography variant="caption" color="text.secondary">{formatGroupQuantity(row.input)}</Typography>
                                                                                            </Stack>
                                                                                        </Grid>
                                                                                        <Grid item xs={12} md={8}>
                                                                                            <Typography variant="subtitle2" gutterBottom>Outputs</Typography>
                                                                                            <Stack spacing={1.25}>
                                                                                                {(row.outputs || []).map((output) => (
                                                                                                    <Stack key={output.groupId} spacing={0.25}>
                                                                                                        <Typography variant="body2" fontWeight={600}>{renderOutputGrade(output)}</Typography>
                                                                                                        <Typography variant="body2">{formatOysterTotal(getGroupOysterCount(output))}</Typography>
                                                                                                        <Typography variant="caption" color="text.secondary">{formatGroupQuantity(output)}</Typography>
                                                                                                    </Stack>
                                                                                                ))}
                                                                                                {(row.outputs || []).length === 0 ? (
                                                                                                    <Typography variant="body2" color="text.secondary">No outputs recorded for this event.</Typography>
                                                                                                ) : null}
                                                                                            </Stack>
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ) : null}
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    );
                                                })}

                                                {!loadingReport && rows.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={7}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {hasSubmitted
                                                                    ? "No grade events matched the selected filters."
                                                                    : "Select a warehouse, adjust any filters you want, and submit to load the report."}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : null}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </React.Fragment>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default BatchGrading;